import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import FullBleedImage from "@/components/FullBleedImage";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";
import { IMG, WORK_PLACEHOLDERS } from "@/lib/images";

const SERVICES = [
  {
    number: "01",
    title: "Website Design",
    body:
      "We build clean, professional websites tailored to your business — from concept to launch, we handle everything.",
  },
  {
    number: "02",
    title: "Hosting & Maintenance",
    body:
      "We keep your site running, updated, protected, and looking sharp every month so you never have to think about it.",
  },
  {
    number: "03",
    title: "Social Media Marketing",
    body:
      "Art-directed content, considered cadences, and storytelling rooted in place. Social that feels like your brand — and speaks directly to your SLO community.",
  },
];

export default function Home() {
  useEffect(() => {
    document.title = "Bishop Creative — Local roots. Lasting impressions.";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative w-full"
      >
        <FullBleedImage
          src={IMG.heroHome}
          alt="Vineyard hills at dusk on the California Central Coast"
          height="h-[100svh] min-h-[680px]"
          position="center"
        >
          <div className="container-page h-full flex flex-col justify-end pb-16 md:pb-24">
            <Reveal>
              <Eyebrow>Student-run creative studio · San Luis Obispo</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-[-0.02em] max-w-[18ch]">
                Local roots.<br />Lasting impressions.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-[52ch] text-[color:var(--text-2)] text-base md:text-lg leading-[1.7]">
                Bishop Creative is a student-run studio designing cinematic websites,
                keeping them impeccably maintained, and building social presence for 
                local businesses across San Luis Obispo and the Central Coast.
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="btn btn-primary"
                  data-testid="home-hero-primary-cta"
                >
                  Request a Proposal
                </Link>
                <Link
                  to="/services"
                  className="btn btn-ghost"
                  data-testid="home-hero-secondary-cta"
                >
                  View Services
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="mt-16 md:mt-20 hairline max-w-[520px]" />
              <div className="mt-6 flex items-center justify-between max-w-[520px] text-[color:var(--text-3)]">
                <span className="eyebrow">Est. San Luis Obispo</span>
                <span className="eyebrow">Local roots · Lasting impressions</span>
              </div>
            </Reveal>
          </div>
        </FullBleedImage>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="section-pad relative">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <Eyebrow>What we do</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                  Built for local business.<br />Designed to last.
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 text-[color:var(--text-2)] max-w-[40ch] leading-[1.75]">
                  We work closely with each client to make sure every detail of your digital
                  presence is intentional, polished, and built to represent your business the right way.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 mt-8 link-underline text-sm"
                  data-testid="home-services-cta"
                >
                  Explore all services
                  <ArrowUpRight size={16} />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-0 border-t border-[color:var(--hairline)]">
                {SERVICES.map((s, i) => (
                  <Reveal key={s.number} delay={i * 0.08}>
                    <div
                      className={`py-10 md:py-12 md:px-8 ${
                        i !== 0 ? "md:border-l border-[color:var(--hairline)]" : ""
                      } border-b md:border-b-0 border-[color:var(--hairline)]`}
                    >
                      <div className="font-mono text-xs tracking-[0.24em] text-[color:var(--text-3)]">
                        {s.number}
                      </div>
                      <h3 className="mt-6 font-display text-2xl md:text-[26px] leading-[1.15] text-[color:var(--text-1)]">
                        {s.title}
                      </h3>
                      <p className="mt-4 text-sm text-[color:var(--text-2)] leading-[1.75] max-w-[32ch]">
                        {s.body}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH / EDITORIAL SPLIT */}
      <section className="relative" style={{ background: "var(--bg-1)" }}>
        <div className="container-page section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <Reveal>
                <Eyebrow>Our approach</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em] max-w-[20ch]">
                  Your business deserves to be seen.<br />We make sure it is.
                </h2>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="mt-8 max-w-[62ch] space-y-5 text-[color:var(--text-2)] leading-[1.8]">
                  <p>
                    We're local students who know this community because we live in it. 
                    We see which businesses are thriving online and which ones are 
                    invisible — and we know exactly what the difference looks like.
                  </p>
                  <p>
                    Every project starts with a real conversation. We learn your business,
                    your customers, and your goals. Then we build something that actually
                    represents you — not a generic template with your name swapped in.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.22}>
                <blockquote className="mt-12 border-l border-[color:rgba(198,168,106,0.65)] pl-6 max-w-[48ch]">
                  <p className="font-display text-2xl md:text-[28px] leading-[1.25] text-[color:var(--text-1)]">
                    “We don't just build websites. We put your business on the map — and keep it there.”
                  </p>
                  <div className="mt-4 eyebrow text-[color:var(--text-3)]">
                    Our Promise
                  </div>
                </blockquote>
              </Reveal>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <Reveal delay={0.12}>
                <div className="relative aspect-[3/4] overflow-hidden rounded-[2px]">
                  <img
                    src={IMG.vineyardRows}
                    alt="Morning light on vineyard rows"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(11,10,8,0.15) 0%, rgba(11,10,8,0.55) 100%)",
                    }}
                  />
                  <div className="noise-overlay" />
                  <figcaption className="absolute left-4 bottom-4 right-4 flex items-center justify-between">
                    <span className="eyebrow text-[color:var(--text-2)]">
                      Paso Robles · Dusk
                    </span>
                    <span className="eyebrow text-[color:var(--text-3)]">
                      Fig. 01
                    </span>
                  </figcaption>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      {/*<section className="section-pad">
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Reveal>
                <Eyebrow>Selected work</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                  A small, growing<br />portfolio.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.16}>
              <p className="max-w-[42ch] text-[color:var(--text-2)] leading-[1.75]">
                A preview of projects in development with Central Coast
                partners. Full case studies arrive with each season.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {WORK_PLACEHOLDERS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <a
                  href="#"
                  className="group block"
                  data-testid={`home-work-${i}`}
                  onClick={(e) => e.preventDefault()}
                >
                  <div className="relative aspect-[4/5] md:aspect-[4/3] overflow-hidden">
                    <img
                      src={w.image}
                      alt={w.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.03]"
                    />
                    <div
                      className="absolute inset-0"
                      aria-hidden
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(11,10,8,0.05) 0%, rgba(11,10,8,0.55) 100%)",
                      }}
                    />
                    <div className="noise-overlay" />
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-6">
                    <div>
                      <h3 className="font-display text-2xl md:text-[28px] leading-[1.15] text-[color:var(--text-1)] group-hover:text-[color:var(--brass)] transition-colors duration-300">
                        {w.title}
                      </h3>
                      <div className="mt-2 eyebrow">{w.category}</div>
                    </div>
                    <ArrowUpRight
                      size={20}
                      className="shrink-0 mt-2 text-[color:var(--text-3)] group-hover:text-[color:var(--brass)] transition-colors duration-300"
                    />
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>*/}

      {/* CTA BAND */}
      {/*<section className="relative overflow-hidden" style={{ background: "var(--bg-1)" }}>
        <div className="absolute inset-0" aria-hidden style={{
          background:
            "linear-gradient(135deg, rgba(176,122,90,0.10), transparent 55%)",
        }} />
        <div className="noise-overlay" />
        <div className="container-page section-pad relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <Eyebrow>Commissioning</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.01em] max-w-[22ch]">
                  We take on a few projects each season. The next opens this spring.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Reveal delay={0.16}>
                <Link
                  to="/contact"
                  className="btn btn-primary"
                  data-testid="home-cta-band"
                >
                  Check Availability
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>*/}
    </div>
  );
}
